import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";

// Register fonts with bold variant
Font.register({
  family: "Helvetica-Regular",
  src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf",
});

Font.register({
  family: "Helvetica-Bold",
  src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf",
});

Font.register({
  family: "NotoSans",
  src: "https://fonts.gstatic.com/s/notosans/v27/o-0IIpQlx3QUlC5A4PNb4g.ttf",
});
// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica-Regular", // Default to regular font
  },
  currency: {
    fontFamily: "NotoSans",
    fontSize: 12,
    textAlign: "right",
  },
  headerSection: {
    marginBottom: 10,
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold", // Use bold font explicitly
    marginBottom: 2,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerInfo: {
    width: "70%",
  },
  qrCode: {
    width: 60,
    height: 60,
    alignSelf: "flex-end",
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    borderBottomStyle: "solid",
    marginVertical: 8,
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  infoCol: {
    width: "48%",
  },
  labelValueRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  infoLabel: {
    width: "40%",
    fontFamily: "Helvetica-Regular", // Regular font for labels
  },
  boldText: { fontFamily: "Helvetica-Bold" },
  infoValue: {
    width: "60%",
    fontFamily: "Helvetica-Bold", // Bold font for values
  },
  table: {
    marginTop: 10,
    marginBottom: 10,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
    backgroundColor: "#f5f5f5",
    padding: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    borderBottomStyle: "solid",
    padding: 5,
  },
  colSrNo: {
    width: "8%",
  },
  colDesc: {
    width: "52%",
  },
  colQty: {
    width: "20%",
    textAlign: "center",
  },
  colAmount: {
    width: "20%",
    textAlign: "right",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 2,
    marginBottom: 2,
  },
  totalLabel: {
    width: "30%",
    fontFamily: "Helvetica-Bold", // Bold font for totals
    textAlign: "right",
    paddingRight: 10,
  },
  totalValue: {
    width: "20%",
    textAlign: "right",
  },
  amountWords: {
    marginTop: 2,
    textAlign: "right",
    fontStyle: "italic",
    fontSize: 9,
  },
  paymentSection: {
    marginTop: 15,
  },
  paymentTitle: {
    fontFamily: "Helvetica-Bold", // Bold font for payment title
    marginBottom: 5,
  },
  paymentRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  paymentLabel: {
    width: "50%",
    fontFamily: "Helvetica-Regular", // Regular font for payment labels
  },
  paymentValue: {
    width: "50%",
    fontFamily: "Helvetica-Bold", // Bold font for payment values
  },
  footer: {
    marginTop: 15,
    borderTopWidth: 1,
    borderTopColor: "#ccc",
    borderTopStyle: "solid",
    paddingTop: 10,

    flexDirection: "row",
    justifyContent: "space-between",
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 15,
  },
  button: {
    padding: 10,
    marginLeft: 10,
    borderRadius: 5,
    textAlign: "center",
  },
  leftBorder: {
    borderLeftWidth: 1,
    borderLeftColor: "#ccc", // or any color you want
    marginLeft: 10, // optional, for spacing
    paddingLeft: 10, // optional, for spacing after the border
  },
  twoColRowPaymentSection: {
    flexDirection: "row",
    justifyContent: "space-between",
  },

  paymentCol: {
    width: "48%", // or 50% if you prefer
  },
});

// Props type
interface Service {
  srNo: number;
  description: string;
  qty: number;
  amount: number;
}

interface InvoicePdfProps {
  invoice: {
    clinicName: string;
    clinicAddress: {
      address: string;
      streetAddress: string;
      locality: string;
      city: string;
      state: string;
      zipCode: string;
    };
    clinicPhone: string;
    clinicEmail: string;
    website: string;
    gstNumber: string;
    patientName: string;
    patientId: string;
    patientMobile: string;
    doctorName: string;
    doctorRegNo: string;
    doctorSpeciality: string;
    consultInvAmount: number;
    receiptNo: string;
    receiptDate: string;
  };
  services: Service[];
  paymentDetails: {
    modeOfPayment: string;
    cardType?: string;
    transactionNo?: string;
    bankName?: string;
  };
  printedBy: string;
  printDateTime: string;
  invoiceDate: string;
  qrCodeData: string; // Optional QR code data
  website: string;
}

const InvoicePdf: React.FC<InvoicePdfProps> = ({
  invoice,
  services,
  paymentDetails,
  printedBy,
  printDateTime,
  invoiceDate,
  qrCodeData,
  website,
}) => {
  // Format full address
  const fullAddress = [
    invoice.clinicAddress.streetAddress,
    invoice.clinicAddress.locality,
    invoice.clinicAddress.city,
    invoice.clinicAddress.state,
    invoice.clinicAddress.zipCode,
  ]
    .filter(Boolean)
    .join(", ");

  const sampleQr = `https://api.qrserver.com/v1/create-qr-code/?data=${qrCodeData}`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with title and close button */}

        {/* Clinic information and QR code */}
        <View style={styles.headerRow}>
          <View style={styles.headerInfo}>
            <Text style={styles.headerTitle}>{invoice.clinicName}</Text>
            <Text>{invoice.clinicAddress.address}</Text>
            <Text>{fullAddress}</Text>
            <Text>
              Phone: {invoice.clinicPhone} | Email: {invoice.clinicEmail}
            </Text>
            <Text>
              Website: {website}
              {invoice.gstNumber && ` | GST Number: ${invoice.gstNumber}`}
            </Text>
          </View>
          <Image src={sampleQr} style={styles.qrCode} />
        </View>

        <View style={{ alignSelf: "flex-end" }}>
          <Text>Date: {invoiceDate}</Text>
        </View>

        <View style={styles.divider} />

        {/* Patient & Doctor Info */}
        <View style={styles.infoRow}>
          <View style={styles.infoCol}>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Patient Name:</Text>
              <Text style={styles.infoValue}>{invoice.patientName}</Text>
            </View>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Registration No:</Text>
              <Text style={styles.infoValue}>{invoice.patientId}</Text>
            </View>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Contact Number:</Text>
              <Text style={styles.infoValue}>{invoice.patientMobile}</Text>
            </View>
          </View>

          <View style={[styles.infoCol, styles.leftBorder]}>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Primary Doctor:</Text>
              <Text style={styles.infoValue}>{invoice.doctorName}</Text>
            </View>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Doctor Reg. No:</Text>
              <Text style={styles.infoValue}>{invoice.doctorRegNo}</Text>
            </View>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Department:</Text>
              <Text style={styles.infoValue}>{invoice.doctorSpeciality}</Text>
            </View>
          </View>
        </View>

        {/* Services Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.colSrNo, styles.boldText]}>Sr No</Text>
            <Text style={[styles.colDesc, styles.boldText]}>
              Service Description
            </Text>
            <Text style={[styles.colQty, styles.boldText]}>Qty</Text>
            <Text style={[styles.colAmount, styles.boldText]}>Amount</Text>
          </View>

          {services.map((service) => (
            <View style={styles.tableRow} key={service.srNo}>
              <Text style={styles.colSrNo}>{service.srNo}</Text>
              <Text style={styles.colDesc}>{service.description}</Text>
              <Text style={styles.colQty}>{service.qty}</Text>

              <Text style={styles.colAmount}>
                {" "}
                <Text style={styles.currency}>{"\u20B9 "}</Text>
                {service.amount}
              </Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Subtotal:</Text>

          <Text style={styles.totalValue}>
            <Text style={styles.currency}>{"\u20B9 "}</Text>
            {services[0].amount}
          </Text>
        </View>

        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Net Payable Amount:</Text>

          <Text style={styles.totalValue}>
            <Text style={styles.currency}>{"\u20B9 "}</Text>
            {services[0].amount}
          </Text>
        </View>

        {/* <Text style={styles.amountWords}>Amount in Words: {amountInWords}</Text> */}

        {/* Payment Details */}
        <View style={styles.paymentSection}>
          <Text style={styles.paymentTitle}>Payment Details</Text>

          {/* 2 Column Layout */}
          <View style={styles.twoColRowPaymentSection}>
            {/* Left Column */}
            <View style={styles.paymentCol}>
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>Mode of Payment:</Text>
                <Text style={styles.paymentValue}>
                  {paymentDetails.modeOfPayment}
                </Text>
              </View>

              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>Receipt Number:</Text>
                <Text style={styles.paymentValue}>{invoice.receiptNo}</Text>
              </View>

              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>Receipt Date:</Text>
                <Text style={styles.paymentValue}>
                  {invoiceDate?.replaceAll("-", "/")}
                </Text>
              </View>
            </View>

            {/* Right Column – Only if mode != Cash */}
            {paymentDetails.modeOfPayment !== "CASH" && (
              <View style={styles.paymentCol}>
                <View style={styles.paymentRow}>
                  <Text style={styles.paymentLabel}>Bank Name:</Text>
                  <Text style={styles.paymentValue}>
                    {paymentDetails.bankName}
                  </Text>
                </View>

                {paymentDetails.modeOfPayment === "CARD" && (
                  <View style={styles.paymentRow}>
                    <Text style={styles.paymentLabel}>Card Type:</Text>
                    <Text style={styles.paymentValue}>
                      {paymentDetails.cardType}
                    </Text>
                  </View>
                )}

                <View style={styles.paymentRow}>
                  <Text style={styles.paymentLabel}>Transaction Number:</Text>
                  <Text style={styles.paymentValue}>
                    {paymentDetails.transactionNo}
                  </Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Bill Printed By: {printedBy} using IC4.health</Text>
          <Text>Date/Time of Printing: {printDateTime}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePdf;
